import { useState, useCallback, useMemo } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_CART, GET_CLOSEST_STORES_FROM_ZIPCODE, GET_LOCATION_FROM_COORDS } from 'mocks/queries';
import { logError } from 'commons/logger';
import { isPhysicalShowroom } from 'components/consumer/StoreLocatorPage/helpers';
import useSetZipCodeFromUser from 'global-state/zipCodeFromUser/useSetZipCodeFromUser';
const EMPTY_STORES = [];
const useShowroomLocator = ({
  limit = 10
} = {}) => {
  const apolloClient = useApolloClient();
  const [searchZipcode, setSearchZipcode] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isResolvingPlace, setIsResolvingPlace] = useState(false);
  const [placeSearchError, setPlaceSearchError] = useState(null);
  const setZipCodeFromUser = useSetZipCodeFromUser();
  const {
    data,
    loading,
    error
  } = useQuery(GET_CLOSEST_STORES_FROM_ZIPCODE, {
    variables: {
      zipcode: searchZipcode,
      limit
    },
    skip: !hasSearched || !searchZipcode,
    errorPolicy: 'all'
  });
  const rawStores = data?.getClosestStoresFromZipcode || EMPTY_STORES;
  const stores = useMemo(() => rawStores.filter(row => isPhysicalShowroom(row?.storeData)), [rawStores]);
  const handleSearch = useCallback(zipcode => {
    const trimmedZipCode = zipcode.trim();
    setPlaceSearchError(null);
    setSearchZipcode(trimmedZipCode);
    setHasSearched(true);
    setZipCodeFromUser(trimmedZipCode);
    apolloClient.refetchQueries({
      include: [GET_CART]
    }).catch(refetchError => {
      logError(refetchError, 'Failed to refetch GET_CART after showroom zip search');
    });
  }, [apolloClient, setZipCodeFromUser]);
  const handleSearchByPlace = useCallback(async ({
    lat,
    lng
  }, {
    onZipResolved
  } = {}) => {
    setPlaceSearchError(null);
    setIsResolvingPlace(true);
    setHasSearched(true);
    try {
      const {
        data: locationData
      } = await apolloClient.query({
        query: GET_LOCATION_FROM_COORDS,
        variables: {
          latitude: lat,
          longitude: lng
        },
        fetchPolicy: 'network-only'
      });
      const resolvedZip = locationData?.getLocationFromCoords?.zip;
      if (resolvedZip) {
        onZipResolved?.(resolvedZip);
        handleSearch(resolvedZip);
      } else {
        setSearchZipcode('');
      }
    } catch (searchByPlaceError) {
      logError(searchByPlaceError, 'Failed to resolve city/state selection to a zip code for showroom search');
      setPlaceSearchError(searchByPlaceError);
    } finally {
      setIsResolvingPlace(false);
    }
  }, [apolloClient, handleSearch]);
  const handleCloseResults = useCallback(() => {
    setHasSearched(false);
  }, []);
  const handleViewAll = useCallback(() => {
    setSearchZipcode('');
    setHasSearched(false);
  }, []);
  return {
    stores,
    loading: loading || isResolvingPlace,
    error: placeSearchError || error,
    hasSearched,
    handleSearch,
    handleSearchByPlace,
    handleCloseResults,
    handleViewAll
  };
};
export default useShowroomLocator;